import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PMProvider } from '../../contexts/PMContext';
import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import { pmEquipmentConfigurationPath } from '../../utils/pmRoutes';
import { PMStatusCards } from './PMStatusCards';
import { PMServiceQueue } from './PMServiceQueue';
import { PMSidebarPanels } from './PMSidebarPanels';
import { PMEquipmentConfiguration } from './PMEquipmentConfiguration';
import { PMInspectionProcess } from './PMInspectionProcess';
import { PMReportsContent } from './PMReportsContent';
import { PMReportDetail } from './PMReportDetail';
import '../../css/PMDashboard.css';
import { Btn, IconPlus, PageHeader, TabNav } from '../ui';

type PMTab = 'dashboard' | 'reports';

type PMSubView =
    | { type: 'none' }
    | { type: 'configure'; unitId: string }
    | { type: 'inspect'; unitId: string; serviceLevelIds: string[] }
    | { type: 'report-detail'; reportId: string };

/**
 * Main container for the PM module. Rendered when page === "pm management" in Dashboard.
 * - Renders top-level tabs: PM Overview, PM Reports
 * - Manages internal navigation state for sub-views (configure, inspect, report detail)
 * - Wraps content in PMContext provider
 *
 * Validates: Requirements 7.1, 7.2, 10.1, 12.1
 */
const inspectViewFromRoute = (unitId?: string, serviceLevelIds?: string): PMSubView => {
    const ids = (serviceLevelIds ?? '').split(',').filter(Boolean);
    if (!unitId || ids.length === 0) return { type: 'none' };
    return { type: 'inspect', unitId, serviceLevelIds: ids };
};

export const PMDashboardContent = () => {
    const { companyUser, companyOwner, businessAdmin } = useUserContext();
    const canManage = hasPermission(companyOwner, companyUser, Permission.MANAGE_PM, businessAdmin);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { unitId: routeUnitId, serviceLevelIds: routeServiceLevelIds } = useParams();
    const legacyQueryUnitId = searchParams.get('unitId')?.trim() ?? '';
    const [activeTab, setActiveTab] = useState<PMTab>('dashboard');
    const [subView, setSubView] = useState<PMSubView>(() => {
        const inspect = inspectViewFromRoute(routeUnitId, routeServiceLevelIds);
        if (inspect.type === 'inspect') return inspect;
        if (routeUnitId) return { type: 'configure', unitId: routeUnitId };
        return { type: 'none' };
    });

    // Legacy email / deep links used /pm-management?unitId=… — prefer /pm-management/:unitId
    useEffect(() => {
        if (!legacyQueryUnitId || routeServiceLevelIds) return;
        navigate(pmEquipmentConfigurationPath(legacyQueryUnitId), { replace: true });
    }, [legacyQueryUnitId, routeServiceLevelIds, navigate]);

    useEffect(() => {
        const next = inspectViewFromRoute(routeUnitId, routeServiceLevelIds);
        if (next.type === 'inspect') {
            setActiveTab('dashboard');
            setSubView(next);
            return;
        }
        if (routeUnitId) {
            setActiveTab('dashboard');
            setSubView({ type: 'configure', unitId: routeUnitId });
            return;
        }
        // No unit in path: clear URL-driven views; keep empty-unit "Add Configuration"
        setSubView((current) => {
            if (current.type === 'inspect') return { type: 'none' };
            if (current.type === 'configure' && current.unitId) return { type: 'none' };
            return current;
        });
    }, [routeUnitId, routeServiceLevelIds]);

    const tabs: { id: PMTab; label: string }[] = [
        { id: 'dashboard', label: 'PM Overview' },
        { id: 'reports', label: 'PM Reports' },
    ];

    const handleTabChange = (tabId: PMTab) => {
        setActiveTab(tabId);
        setSubView({ type: 'none' });
        if (routeUnitId || routeServiceLevelIds) {
            navigate('/pm-management');
        }
    };

    const leaveConfiguration = () => {
        setSubView({ type: 'none' });
        if (routeUnitId && !routeServiceLevelIds) {
            navigate('/pm-management');
        }
    };

    const handleSelectUnit = (unitId: string) => {
        if (!unitId) {
            setSubView({ type: 'configure', unitId: '' });
            return;
        }
        navigate(pmEquipmentConfigurationPath(unitId));
    };

    const handlePerformService = (unitId: string, serviceLevelIds: string[]) => {
        const ids = serviceLevelIds.filter(Boolean);
        if (!unitId || ids.length === 0) return;
        setActiveTab('dashboard');
        setSubView({ type: 'inspect', unitId, serviceLevelIds: ids });
        navigate(`/pm-management/inspect/${unitId}/${ids.join(',')}`);
    };

    const leaveInspect = () => {
        const historyIndex = window.history.state?.idx;
        if (typeof historyIndex === 'number' && historyIndex > 0) {
            navigate(-1);
            return;
        }
        navigate('/pm-management');
    };

    // Render sub-views when navigating to configure/inspect/report detail
    const renderSubView = () => {
        switch (subView.type) {
            case 'configure':
                return (
                    <PMEquipmentConfiguration
                        unitId={subView.unitId}
                        onBack={leaveConfiguration}
                        onPerformService={handlePerformService}
                    />
                );
            case 'inspect':
                return (
                    <PMInspectionProcess
                        unitId={subView.unitId}
                        serviceLevelIds={subView.serviceLevelIds}
                        onBack={leaveInspect}
                        onComplete={leaveInspect}
                    />
                );
            case 'report-detail':
                return <PMReportDetail reportId={subView.reportId} onBack={() => setSubView({ type: 'none' })} />;
            default:
                return null;
        }
    };

    const renderDashboardTab = () => {
        if (subView.type !== 'none') {
            return renderSubView();
        }

        return (
            <div className="pm-dashboard-home">
                <div className="pm-dashboard-main">
                    <PMStatusCards />
                    <PMServiceQueue onSelectUnit={handleSelectUnit} />
                </div>
                <PMSidebarPanels onSelectUnit={handleSelectUnit} />
            </div>
        );
    };

    const renderReportsTab = () => {
        if (subView.type === 'report-detail') {
            return renderSubView();
        }
        return <PMReportsContent onViewReport={(reportId) => setSubView({ type: 'report-detail', reportId })} />;
    };

    const hideModuleChrome = subView.type === 'configure' || subView.type === 'inspect';

    return (
        <PMProvider>
            <div className="page-container pm-dashboard-container">
                {!hideModuleChrome && (
                    <PageHeader
                        title="Preventive Maintenance"
                        actions={
                            subView.type === 'none' && canManage ? (
                                <Btn
                                    variant="primary"
                                    buttonIcon={<IconPlus />}
                                    buttonText="Add Configuration"
                                    buttonOnClick={() => setSubView({ type: 'configure', unitId: '' })}
                                />
                            ) : undefined
                        }
                    />
                )}

                {!hideModuleChrome && (
                    <TabNav
                        variant="pills"
                        size="lg"
                        ariaLabel="Preventive maintenance sections"
                        className="tab-nav-stand-alone pm-dashboard-tabs"
                        tabs={tabs}
                        activeTabId={activeTab}
                        onTabChange={(tabId) => handleTabChange(tabId as PMTab)}
                    />
                )}

                {activeTab === 'dashboard' && renderDashboardTab()}
                {activeTab === 'reports' && renderReportsTab()}
            </div>
        </PMProvider>
    );
};
